import { Component, forwardRef, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { catchError, delay, map, switchMap } from 'rxjs/operators';

import { getSweetAlertOptions } from '@commons/utility/util';

import { show_popup, TITLE_ERROR } from '@shared/utility/popup-error';

import { DocumentInfo } from '@core/model/document-info';
import { IdName } from '@core/model/id-name';
import { retryWithDelay } from '@core/operators/retry-delay';

import Swal, { SweetAlertCustomClass } from 'sweetalert2';
import { DocumentVerificationService } from './document-verification.service';

import { INPUT_PATTERNS_CONSTANT } from './rapi-doc-info.constant';

@Component({
  selector: 'app-rapi-doc-info',
  templateUrl: './rapi-doc-info.component.html',
  styleUrls: ['./rapi-doc-info.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => RapiDocInfoComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RapiDocInfoComponent),
      multi: true,
    },
  ],
})
export class RapiDocInfoComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @Input() disableDocNumberControl: boolean = false;
  @Output() enableFields = new EventEmitter<boolean>();
  @Input('docType') docType: IdName;
  @Output() setCellPhone = new EventEmitter<boolean>();

  form: FormGroup;
  subscriptions: Subscription[] = [];
  existingValidationMsg: string;
  invalidNumberMsg: string;

  constructor(
    private formBuilder: FormBuilder,
    private docVerificationService: DocumentVerificationService,
    private router: Router
  ) {
    this.form = this.formBuilder.group(
      {
        docNumber: ['', Validators.required],
      },
      {
        updateOn: 'blur',
      }
    );
    let id_detail = sessionStorage.getItem('cai');
    if (id_detail != null && id_detail != 'undefined') {
      this.form.controls['docNumber'].markAsTouched({ onlySelf: true });
      this.form.controls['docNumber'].setValidators(null);
    }

    this.subscriptions.push(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );

    this.form.controls['docNumber'].setAsyncValidators(this.asyncValidateDocPipe());
    this.form.controls['docNumber'].statusChanges.subscribe(() => {
      this.form.updateValueAndValidity();
    });
  }

  onChange: any = () => {};
  onTouched: any = () => {};

  get docNumberControl() {
    return this.form?.controls.docNumber;
  }

  get value(): DocumentInfo {
    return this.form.value;
  }

  set value(value: DocumentInfo) {
    this.form.setValue(value);
    this.onChange(value);
    this.onTouched();
  }

  writeValue(value: any): void {
    if (value) {
      this.value = value;
    }

    if (value === null) {
      this.form.reset();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  ngOnInit() {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  ngOnChanges() {
    this.docNumberControl?.updateValueAndValidity();
    if (this.disableDocNumberControl) {
      this.docNumberControl.disable();
    } else {
      this.docNumberControl.enable();
    }
  }

  validate(_: FormControl) {
    if (!this.docType || !this.docNumberControl.value || this.docNumberControl.invalid) {
      return this.form.valid ? null : { docInfo: { valid: false } };
    }
  }

  validateInput(event: any) {}

  change(item: any) {
    this.docNumberControl.updateValueAndValidity();
  }

  private _asyncValidateDocNumber(docNumber: string): Observable<ValidationErrors | null> | null {
    return this.docVerificationService.validateDocNumber(docNumber).pipe(
      map((result) => {
        if (result.response == false) {
          this.invalidNumberMsg = result.message;
          return {
            invalidNumber: true,
          };
        } else {
          return null;
        }
      })
    );
  }

  private _asyncValidateAccountExistence(
    docType: number,
    docNumber: string
  ): Observable<ValidationErrors | null> | null {
    return this.docVerificationService.existsByDocTypeAndNumber(docType, docNumber).pipe(
      map((result) => {
        if (result.response == true) {
          this._showExistenceMessage(result.message);
          return {
            valid: false,
          };
        } else {
          this.setCellPhone.emit(true);
          return null;
        }
      })
    );
  }

  private _showExistenceMessage(message: string) {
    let id_detail = sessionStorage.getItem('cai');
    let options = getSweetAlertOptions(
      message,
      'INICIAR SESIÓN',
      id_detail == null || id_detail === 'undefined',
      'RECTIFICAR DOCUMENTO'
    );

    Swal.fire(options).then((result) => {
      if (result.value) {
        this.router.navigate(['/login']);
      } else {
        this.enableFieldsEvent();
      }
    });
  }

  enableFieldsEvent() {
    this.docNumberControl.reset();
    this.enableFields.emit(false);
  }

  private isValidPattern(docNumber: string | null): boolean {
    return docNumber ? INPUT_PATTERNS_CONSTANT.NUMBER_RANGE.test(docNumber) : false;
  }

  asyncValidateDocPipe(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> | null => {
      if (!this.isValidPattern(control.value)) {
        return of({ pattern: true });
      }

      if (control.value != null) {
        return of(control.value).pipe(
          delay(100),
          switchMap(() => {
            if (this.docNumberControl.value && this.docNumberControl.value.length != 0) {
              return this._asyncValidateDocNumber(this.docNumberControl.value);
            }
          }),
          switchMap((value) => {
            if (value == null) {
              if (!this.docType) {
                return of({ docType: true });
              }
              return this._asyncValidateAccountExistence(this.docType.id, this.docNumberControl.value);
            }
            return of(value);
          }),
          retryWithDelay(1000, 3),
          catchError((error: Error) => {
            show_popup(
              TITLE_ERROR,
              'Lo sentimos, ocurrió un error al validar tu documento de identidad. Inténtalo de nuevo  P-16'
            );
            this.docNumberControl.setValue(null);
            return EMPTY;
          })
        );
      }
      return EMPTY;
    };
  }
  checkIsNumber(e) {
    e = e || window.event;
    var charCode = typeof e.which == 'undefined' ? e.keyCode : e.which;
    var charStr = String.fromCharCode(charCode);

    if (!charStr.match(INPUT_PATTERNS_CONSTANT.NUMBER)) e.preventDefault();
  }
}
